import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search as SearchIcon, X } from "lucide-react";
import { listServices, listPackages, listPortfolioFeatured } from "../../lib/api";
import { t } from "../../lib/i18n";

/**
 * Full-screen search overlay. Lazily loads services + packages + portfolio on
 * first open, filters client-side as the user types. Enter navigates to
 * /search?q=... for a persistent results page.
 */
export default function SearchOverlay({ open, onOpenChange }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [data, setData] = useState({ services: [], packages: [], portfolio: [] });
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!open || loaded) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const [services, packages, portfolio] = await Promise.all([
          listServices(),
          listPackages({ limit: 50 }),
          listPortfolioFeatured({ limit: 60 }),
        ]);
        if (cancelled) return;
        setData({ services, packages, portfolio });
        setLoaded(true);
      } catch {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, loaded]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onOpenChange(false);
      if (e.key === "Enter" && query.trim().length >= 2) {
        navigate(`/search?q=${encodeURIComponent(query.trim())}`);
        onOpenChange(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, query, navigate, onOpenChange]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 2) return null;
    const match = (s) => typeof s === "string" && s.toLowerCase().includes(q);
    return {
      services: data.services.filter(
        (s) => match(s.name) || match(s.tagline) || match(s.description) || (s.occasions || []).some(match),
      ),
      packages: data.packages.filter((p) => match(p.name) || match(p.summary)),
      portfolio: data.portfolio.filter(
        (i) => match(i.title) || (i.tags || []).some(match),
      ),
    };
  }, [query, data]);

  if (!open) return null;

  return (
    <div
      data-testid="search-overlay"
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[90] flex flex-col bg-[#FAF7F2]"
    >
      <div className="border-b border-[#EAE3D2]">
        <div className="mx-auto flex max-w-3xl items-center gap-3 px-5 py-4 sm:px-6">
          <SearchIcon className="h-5 w-5 text-[#1A1A1A]/50" />
          <input
            type="text"
            autoFocus
            data-testid="search-input"
            placeholder={t("search.placeholder")}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="flex-1 bg-transparent text-base text-[#1A1A1A] placeholder-[#1A1A1A]/40 outline-none sm:text-lg"
          />
          <button
            type="button"
            data-testid="search-close"
            aria-label={t("nav.close")}
            onClick={() => onOpenChange(false)}
            className="rounded-full p-2 text-[#1A1A1A]/70 transition hover:bg-[#EAE3D2]/40 hover:text-[#1A1A1A]"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      <div className="mx-auto w-full max-w-3xl flex-1 overflow-y-auto px-5 py-8 sm:px-6">
        {!results ? (
          <div>
            <p
              data-testid="search-heading"
              className="font-display text-2xl text-[#1A1A1A]"
            >
              {t("search.heading")}
            </p>
            <p className="mt-2 text-sm text-[#1A1A1A]/55">
              {t("search.placeholder")}
            </p>
          </div>
        ) : (
          <ResultsBlocks
            results={results}
            onPick={() => onOpenChange(false)}
            onCtaPlan={() => {
              onOpenChange(false);
              navigate("/plan");
            }}
          />
        )}
      </div>
    </div>
  );
}

function ResultsBlocks({ results, onPick, onCtaPlan }) {
  const navigate = useNavigate();
  const { services, packages, portfolio } = results;
  const total = services.length + packages.length + portfolio.length;

  if (total === 0) {
    return (
      <div data-testid="search-empty" className="py-14 text-center">
        <p className="font-display text-2xl text-[#1A1A1A]">
          {t("search.emptyTitle")}
        </p>
        <p className="mx-auto mt-3 max-w-md text-sm text-[#1A1A1A]/60">
          {t("search.emptySubtitle")}
        </p>
        <button
          type="button"
          onClick={onCtaPlan}
          data-testid="search-empty-cta"
          className="mt-7 inline-flex items-center rounded-full border border-[#1A1A1A] px-6 py-2.5 text-xs uppercase tracking-[0.24em] text-[#1A1A1A] transition hover:bg-[#1A1A1A] hover:text-[#FAF7F2]"
        >
          {t("search.emptyCta")}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-10">
      {services.length ? (
        <Group title={t("search.groups.services")} testid="search-group-services">
          {services.map((s) => (
            <Row
              key={s.id}
              testid={`search-result-service-${s.slug}`}
              onClick={() => {
                onPick();
                navigate(`/services/${s.slug}`);
              }}
              title={s.name}
              subtitle={s.tagline}
            />
          ))}
        </Group>
      ) : null}
      {packages.length ? (
        <Group title={t("search.groups.packages")} testid="search-group-packages">
          {packages.map((p) => (
            <Row
              key={p.id}
              testid={`search-result-package-${p.id}`}
              onClick={() => {
                onPick();
                navigate(`/packages/${p.id}`);
              }}
              title={p.name}
              subtitle={p.summary}
            />
          ))}
        </Group>
      ) : null}
      {portfolio.length ? (
        <Group title={t("search.groups.portfolio")} testid="search-group-portfolio">
          <div className="grid grid-cols-3 gap-3 sm:grid-cols-5">
            {portfolio.slice(0, 15).map((i) => (
              <img
                key={i.id}
                src={i.thumbnail_url || i.url}
                alt={i.title || "Portfolio result"}
                data-testid={`search-result-portfolio-${i.id}`}
                className="aspect-square w-full rounded-md object-cover"
              />
            ))}
          </div>
        </Group>
      ) : null}
    </div>
  );
}

const Group = ({ title, children, testid }) => (
  <section data-testid={testid}>
    <p className="mb-3 text-[10px] uppercase tracking-[0.32em] text-[#1A1A1A]/50">
      {title}
    </p>
    {children}
  </section>
);

const Row = ({ title, subtitle, onClick, testid }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    className="flex w-full items-start justify-between gap-4 border-b border-[#EAE3D2] py-3 text-left transition hover:border-[#C8A24B]"
  >
    <div>
      <p className="font-display text-lg text-[#1A1A1A]">{title}</p>
      {subtitle ? (
        <p className="mt-1 line-clamp-1 text-xs text-[#1A1A1A]/55">{subtitle}</p>
      ) : null}
    </div>
    <span className="text-xs uppercase tracking-[0.24em] text-[#1A1A1A]/40">→</span>
  </button>
);
